import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  lawLibraryApi,
  adminLawLibraryApi,
  type JurisdictionSummary,
  type JurisdictionDetail,
  type GlobalTemplate,
} from "../api/lawLibrary";
import {
  Shield,
  FileText,
  ChevronRight,
  ArrowLeft,
  Save,
  Trash2,
  Plus,
  X,
} from "lucide-react";

// ─── Action badge ────────────────────────────────────
function ActionBadge({ action }: { action: string }) {
  const a = action?.toUpperCase() || "";
  let cls = "bg-gray-100 text-gray-700 dark:bg-slate-600 dark:text-slate-300";
  if (a === "REDACT_FULL")
    cls = "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400";
  else if (a.startsWith("TRUNCATE") || a === "KEEP_LAST_4")
    cls = "bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-400";
  else if (a === "VISIBLE")
    cls = "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}
    >
      {action}
    </span>
  );
}

// ─── Tabs ────────────────────────────────────────────
type TabId = "redaction" | "templates";

// ═════════════════════════════════════════════════════
// Main Page
// ═════════════════════════════════════════════════════

const LawLibraryPage: React.FC = () => {
  const { role } = useAuth();
  const isSuperAdmin = role === "SUPERADMIN";

  const [activeTab, setActiveTab] = useState<TabId>("redaction");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redaction rules state
  const [jurisdictions, setJurisdictions] = useState<JurisdictionSummary[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] =
    useState<JurisdictionDetail | null>(null);
  const [editingRules, setEditingRules] = useState<Record<string, any> | null>(
    null
  );
  const [saving, setSaving] = useState(false);

  // Add new jurisdiction
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStateCode, setNewStateCode] = useState("");

  // Templates state
  const [templates, setTemplates] = useState<GlobalTemplate[]>([]);

  // ─── Fetch jurisdictions ───────────────────────────
  const fetchJurisdictions = async () => {
    try {
      setError(null);
      setLoading(true);
      const data = isSuperAdmin
        ? await adminLawLibraryApi.listJurisdictions()
        : await lawLibraryApi.listJurisdictions();
      setJurisdictions(data.jurisdictions);
    } catch (err: any) {
      setError(err.message || "Failed to load jurisdictions");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setError(null);
      const data = isSuperAdmin
        ? await adminLawLibraryApi.listGlobalTemplates({ per_page: 50 })
        : await lawLibraryApi.listGlobalTemplates({ per_page: 50 });
      setTemplates(data.templates);
    } catch (err: any) {
      setError(err.message || "Failed to load templates");
    }
  };

  useEffect(() => {
    fetchJurisdictions();
    fetchTemplates();
  }, []);

  // ─── Open jurisdiction detail ──────────────────────
  const openJurisdiction = async (stateCode: string) => {
    try {
      setError(null);
      const detail = await lawLibraryApi.getJurisdiction(stateCode);
      setSelectedJurisdiction(detail);
      setEditingRules(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ─── Save jurisdiction rules ───────────────────────
  const saveRules = async () => {
    if (!selectedJurisdiction || !editingRules) return;
    try {
      setSaving(true);
      await adminLawLibraryApi.updateJurisdiction(
        selectedJurisdiction.state_code,
        editingRules
      );
      // Refresh
      const detail = await lawLibraryApi.getJurisdiction(
        selectedJurisdiction.state_code
      );
      setSelectedJurisdiction(detail);
      setEditingRules(null);
      fetchJurisdictions();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Add new jurisdiction ──────────────────────────
  const addJurisdiction = async () => {
    if (!newStateCode.trim()) return;
    try {
      setSaving(true);
      await adminLawLibraryApi.createJurisdiction(newStateCode.trim().toUpperCase(), {
        SSN: { action: "REDACT_FULL", description: "Default" },
      });
      setShowAddForm(false);
      setNewStateCode("");
      fetchJurisdictions();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete jurisdiction ───────────────────────────
  const deleteJurisdiction = async (stateCode: string) => {
    if (!confirm(`Delete all rules for ${stateCode}?`)) return;
    try {
      await adminLawLibraryApi.deleteJurisdiction(stateCode);
      setSelectedJurisdiction(null);
      fetchJurisdictions();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ─── Edit a single PII rule in the editing state ──
  const updateRule = (
    fieldType: string,
    key: string,
    value: string | number
  ) => {
    if (!editingRules) return;
    setEditingRules({
      ...editingRules,
      [fieldType]: { ...editingRules[fieldType], [key]: value },
    });
  };

  const addNewRule = () => {
    if (!editingRules) return;
    const name = prompt("PII field type (e.g. DRIVERS_LICENSE):");
    if (!name) return;
    setEditingRules({
      ...editingRules,
      [name.toUpperCase()]: { action: "REDACT_FULL", description: "" },
    });
  };

  const removeRule = (fieldType: string) => {
    if (!editingRules) return;
    const next = { ...editingRules };
    delete next[fieldType];
    setEditingRules(next);
  };

  // ─── Deactivate global template ───────────────────
  const deactivateTemplate = async (templateId: string) => {
    if (!confirm("Deactivate this global template?")) return;
    try {
      await adminLawLibraryApi.deactivateGlobalTemplate(templateId);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ─── Jurisdiction Detail View ─────────────────────
  if (selectedJurisdiction) {
    const rules = editingRules || selectedJurisdiction.rules;
    const isEditing = editingRules !== null;

    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8 font-sans">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => setSelectedJurisdiction(null)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-500 mb-6"
          >
            <ArrowLeft size={16} /> Back to Jurisdictions
          </button>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {selectedJurisdiction.state_code} Redaction Rules
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {selectedJurisdiction.state_code === "FEDERAL"
                  ? "Federal baseline - applied to all jurisdictions"
                  : `State overlay - merged on top of Federal baseline`}
              </p>
            </div>

            {isSuperAdmin && (
              <div className="flex gap-2">
                {!isEditing ? (
                  <button
                    onClick={() =>
                      setEditingRules({ ...selectedJurisdiction.rules })
                    }
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    Edit Rules
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setEditingRules(null)}
                      className="border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveRules}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                    >
                      <Save size={14} />
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </>
                )}
                {selectedJurisdiction.state_code !== "FEDERAL" && (
                  <button
                    onClick={() =>
                      deleteJurisdiction(selectedJurisdiction.state_code)
                    }
                    className="text-red-600 hover:text-red-500 px-3 py-2 rounded-lg text-sm"
                    title="Delete jurisdiction"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Rules table */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
              <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                <tr>
                  <th className="px-6 py-3">PII Field</th>
                  <th className="px-6 py-3">Action</th>
                  <th className="px-6 py-3">Description</th>
                  {isEditing && (
                    <th className="px-6 py-3 text-right">Remove</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {Object.entries(rules).map(([fieldType, config]: [string, any]) => (
                  <tr
                    key={fieldType}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition"
                  >
                    <td className="px-6 py-3 font-medium text-slate-900 dark:text-white">
                      {fieldType}
                    </td>
                    <td className="px-6 py-3">
                      {isEditing ? (
                        <select
                          value={config.action || "REDACT_FULL"}
                          onChange={(e) =>
                            updateRule(fieldType, "action", e.target.value)
                          }
                          className="text-sm border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                        >
                          <option value="REDACT_FULL">REDACT_FULL</option>
                          <option value="TRUNCATE">TRUNCATE</option>
                          <option value="TRUNCATE_YEAR">TRUNCATE_YEAR</option>
                          <option value="KEEP_LAST_4">KEEP_LAST_4</option>
                          <option value="VISIBLE">VISIBLE</option>
                        </select>
                      ) : (
                        <ActionBadge action={config.action} />
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={config.description || ""}
                          onChange={(e) =>
                            updateRule(fieldType, "description", e.target.value)
                          }
                          className="text-sm border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-slate-900 dark:text-white w-full"
                          placeholder="e.g. FRCP 5.2"
                        />
                      ) : (
                        <span className="text-slate-500">
                          {config.description || "-"}
                        </span>
                      )}
                    </td>
                    {isEditing && (
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => removeRule(fieldType)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {isEditing && (
              <div className="p-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={addNewRule}
                  className="text-sm text-blue-600 hover:text-blue-500 flex items-center gap-1"
                >
                  <Plus size={14} /> Add PII Field
                </button>
              </div>
            )}

            {Object.keys(rules).length === 0 && (
              <div className="p-8 text-center text-slate-400">
                No rules defined for this jurisdiction.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Main List View ────────────────────────────────
  if (loading) {
    return (
      <div className="p-8 text-slate-900 dark:text-white">
        Loading Law Library...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Law Library
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              {isSuperAdmin
                ? "Manage global redaction & split rules for all organizations"
                : "View redaction policies and document templates"}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl p-4 text-red-700 dark:text-red-400 text-sm">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-3 text-red-500 hover:text-red-400"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-slate-200 dark:bg-slate-800 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab("redaction")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              activeTab === "redaction"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <span className="flex items-center gap-2">
              <Shield size={14} /> Redaction Rules
            </span>
          </button>
          <button
            onClick={() => setActiveTab("templates")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              activeTab === "templates"
                ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <span className="flex items-center gap-2">
              <FileText size={14} /> Split & Template Rules
            </span>
          </button>
        </div>

        {/* ─── Tab: Redaction Rules ────────────────── */}
        {activeTab === "redaction" && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Jurisdiction Redaction Policies
              </h2>
              {isSuperAdmin && (
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1"
                >
                  <Plus size={14} /> Add Jurisdiction
                </button>
              )}
            </div>

            {/* Add form */}
            {showAddForm && (
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/30 flex items-center gap-3">
                <input
                  type="text"
                  value={newStateCode}
                  onChange={(e) => setNewStateCode(e.target.value.toUpperCase())}
                  placeholder="State code (e.g. NY)"
                  maxLength={5}
                  className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white w-32"
                />
                <button
                  onClick={addJurisdiction}
                  disabled={!newStateCode.trim() || saving}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {saving ? "Creating..." : "Create"}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewStateCode("");
                  }}
                  className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm"
                >
                  Cancel
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                  <tr>
                    <th className="px-6 py-3">Jurisdiction</th>
                    <th className="px-6 py-3 text-right">Rules</th>
                    <th className="px-6 py-3">Last Updated</th>
                    <th className="px-6 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {jurisdictions.map((j) => (
                    <tr
                      key={j.state_code}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition"
                    >
                      <td className="px-6 py-3">
                        <span className="font-medium text-slate-900 dark:text-white">
                          {j.state_code}
                        </span>
                        {j.state_code === "FEDERAL" && (
                          <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
                            BASELINE
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {j.rule_count ?? (j.rules ? Object.keys(j.rules).length : 0)}
                      </td>
                      <td className="px-6 py-3 text-slate-500 text-xs">
                        {j.updated_at
                          ? new Date(j.updated_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => openJurisdiction(j.state_code)}
                          className="text-blue-600 hover:text-blue-500 font-medium text-sm flex items-center gap-1 ml-auto"
                        >
                          View <ChevronRight size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {jurisdictions.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  No jurisdictions configured.
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Tab: Templates ──────────────────────── */}
        {activeTab === "templates" && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Global Document Templates
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Templates with split and redaction rules shared across all
                organizations
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                  <tr>
                    <th className="px-6 py-3">Fingerprint</th>
                    <th className="px-6 py-3">Jurisdiction</th>
                    <th className="px-6 py-3">Doc Type</th>
                    <th className="px-6 py-3 text-right">Split Rules</th>
                    <th className="px-6 py-3 text-right">Redaction Zones</th>
                    <th className="px-6 py-3 text-right">Updates</th>
                    <th className="px-6 py-3">Status</th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-right">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {templates.map((t) => (
                    <tr
                      key={t.id}
                      className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition"
                    >
                      <td className="px-6 py-3 font-mono text-xs">
                        {t.fingerprint_hash.slice(0, 12)}...
                      </td>
                      <td className="px-6 py-3">{t.jurisdiction_code}</td>
                      <td className="px-6 py-3">{t.doc_type || "-"}</td>
                      <td className="px-6 py-3 text-right">
                        {t.split_rules?.length || 0}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {t.redaction_zones?.length || 0}
                      </td>
                      <td className="px-6 py-3 text-right">
                        {t.updated_count}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            t.is_active
                              ? "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400"
                              : "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-400"
                          }`}
                        >
                          {t.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-3 text-right">
                          <button
                            onClick={() => deactivateTemplate(t.id)}
                            className="text-red-500 hover:text-red-400 text-sm"
                            title="Deactivate"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>

              {templates.length === 0 && (
                <div className="p-8 text-center text-slate-400">
                  No global templates found. Templates are created automatically
                  when documents are processed.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LawLibraryPage;
